
import React, { useEffect } from 'react';
import { CONTACT_INFO } from '../constants';

interface PrivacyCookiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab: 'privacy' | 'cookies';
}

const PrivacyCookiesModal: React.FC<PrivacyCookiesModalProps> = ({ isOpen, onClose, initialTab }) => {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    if (isOpen) document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
      <div 
        className="absolute inset-0 bg-primary/20 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />
      
      <div className="relative bg-white w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-[3rem] shadow-2xl border border-accent-light animate-in fade-in zoom-in duration-300 flex flex-col">
        {/* Header */}
        <div className="p-8 md:p-12 border-b border-accent-light flex justify-between items-center bg-liza-bg/50">
          <h2 className="text-3xl font-serif text-primary">Privacy & Cookies</h2>
          <button 
            onClick={onClose}
            className="w-12 h-12 rounded-full flex items-center justify-center border border-accent hover:bg-primary hover:text-white transition-all duration-300 group"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-grow overflow-y-auto p-8 md:p-12 space-y-12">
          
          <section id="privacy" className={initialTab === 'privacy' ? 'scroll-mt-12' : ''}>
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-4 block">Privacyverklaring</span>
            <h3 className="text-2xl font-serif text-primary mb-6">Je privacy is mijn prioriteit</h3>
            <div className="prose prose-sm text-gray-600 font-light leading-relaxed space-y-4">
              <p>Liza Moerman Coach respecteert je privacy en draagt er zorg voor dat de persoonlijke informatie die je ons verschaft altijd vertrouwelijk wordt behandeld.</p>
              
              <h4 className="font-bold text-gray-800 uppercase text-[10px] tracking-widest mt-8">Doeleinden van gegevensverwerking</h4>
              <p>Wanneer je het contactformulier invult of de Stress-Check doet, verwerken wij de volgende gegevens:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Naam en contactgegevens voor het inplannen van een kennismaking.</li>
                <li>Inhoud van je bericht of Stress-Check rapport om de coaching optimaal voor te bereiden.</li>
              </ul>

              <h4 className="font-bold text-gray-800 uppercase text-[10px] tracking-widest mt-8">Bewaartermijn</h4>
              <p>Wij bewaren je gegevens niet langer dan strikt noodzakelijk is om de doelen te realiseren waarvoor je gegevens worden verzameld. Indien er geen coachingstraject volgt, worden je gegevens binnen 6 maanden verwijderd.</p>

              <h4 className="font-bold text-gray-800 uppercase text-[10px] tracking-widest mt-8">Delen met derden</h4>
              <p>Liza Moerman Coach verkoopt je gegevens niet aan derden en verstrekt deze uitsluitend indien dit nodig is voor de uitvoering van onze overeenkomst met je of om te voldoen aan een wettelijke verplichting.</p>
            </div>
          </section>

          <hr className="border-accent-light" />

          <section id="cookies">
            <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-4 block">Cookiebeleid</span>
            <h3 className="text-2xl font-serif text-primary mb-6">Gebruik van Cookies</h3>
            <div className="prose prose-sm text-gray-600 font-light leading-relaxed space-y-4">
              <p>Onze website maakt gebruik van functionele en analytische cookies om de gebruikerservaring te verbeteren en het verkeer op de website te analyseren.</p>
              
              <h4 className="font-bold text-gray-800 uppercase text-[10px] tracking-widest mt-8">Functionele cookies</h4>
              <p>Deze zijn noodzakelijk voor de technische werking van de website en je gebruiksgemak. Ze zorgen ervoor dat de website naar behoren werkt en onthouden bijvoorbeeld je voorkeursinstellingen.</p>

              <h4 className="font-bold text-gray-800 uppercase text-[10px] tracking-widest mt-8">Analytische cookies</h4>
              <p>Wij maken gebruik van geanonimiseerde statistieken (zoals Google Analytics) om inzicht te krijgen in hoe bezoekers de website gebruiken. Deze gegevens zijn niet herleidbaar tot individuele personen.</p>

              <p className="mt-8 italic text-xs">Je kunt je afmelden voor cookies door je internetbrowser zo in te stellen dat deze geen cookies meer opslaat.</p>
            </div>
          </section>

          <div className="bg-liza-bg p-8 rounded-3xl border border-accent-light">
            <h4 className="font-bold text-primary uppercase text-[10px] tracking-widest mb-4">Contact over privacy</h4>
            <p className="text-xs text-gray-500 font-light leading-relaxed">
              Heb je vragen over deze verklaring of wil je je gegevens inzien, corrigeren of laten verwijderen? Neem dan contact op via <span className="font-medium text-primary">{CONTACT_INFO.email}</span>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyCookiesModal;
